import React, { useState, useEffect } from "react";

const CalorieIntakeCalculator = () => {
  const [weight, setWeight] = useState(70);
  const [height, setHeight] = useState(170);
  const [gender, setGender] = useState("male");
  const [unitSystem, setUnitSystem] = useState("metric");
  const [age, setAge] = useState(25);
  const [activityLevel, setActivityLevel] = useState("moderate");
  const [goal, setGoal] = useState("maintain");
  const [result, setResult] = useState({
    maintenanceCalories: "0",
    targetCalories: "0",
    interpretation: "",
    macroBreakdown: "",
  });

  // Calorie Intake Calculation
  const calculateCalorieIntake = () => {
    let w = parseFloat(weight);
    let h = parseFloat(height);
    let a = parseFloat(age);

    if (isNaN(w) || w <= 0 || isNaN(h) || h <= 0 || isNaN(a) || a <= 0) return;

    let bmr;
    if (unitSystem === "metric") {
      if (gender === "male") {
        bmr = 10 * w + 6.25 * h - 5 * a + 5;
      } else {
        bmr = 10 * w + 6.25 * h - 5 * a - 161;
      }
    } else {
      const weightKg = w * 0.453592;
      const heightCm = h * 2.54;
      if (gender === "male") {
        bmr = 10 * weightKg + 6.25 * heightCm - 5 * a + 5;
      } else {
        bmr = 10 * weightKg + 6.25 * heightCm - 5 * a - 161;
      }
    }

    const activityMultipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      veryActive: 1.9,
    };

    const maintenanceCalories = bmr * activityMultipliers[activityLevel];

    let targetCalories;
    let interpretation = "";
    
    if (goal === "lose") {
      targetCalories = maintenanceCalories - 500;
      interpretation = `To lose weight safely, aim for a 500-calorie deficit per day. This typically results in losing about 0.5kg (1lb) per week. Focus on nutrient-dense foods and maintain regular exercise.`;
    } else if (goal === "gain") {
      targetCalories = maintenanceCalories + 500;
      interpretation = `To gain weight healthily, aim for a 500-calorie surplus per day. This typically results in gaining about 0.5kg (1lb) per week. Combine this with strength training to build muscle mass.`;
    } else {
      targetCalories = maintenanceCalories;
      interpretation = `To maintain your current weight, consume your maintenance calories. Balance your diet with proteins, carbohydrates, and healthy fats while staying active.`;
    }

    const protein = Math.round((targetCalories * 0.30) / 4);
    const carbs = Math.round((targetCalories * 0.40) / 4);
    const fats = Math.round((targetCalories * 0.30) / 9);
    
    const macroBreakdown = `Protein: ${protein}g | Carbs: ${carbs}g | Fats: ${fats}g`;

    setResult({
      maintenanceCalories: Math.round(maintenanceCalories).toString(),
      targetCalories: Math.round(targetCalories).toString(),
      interpretation,
      macroBreakdown,
    });
  };

  useEffect(() => {
  calculateCalorieIntake();
}, [weight, height, age, gender, activityLevel, goal, unitSystem]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M16 8v5l3 3"></path>
            </svg>
          </div>
          <h1>Calorie Intake Calculator</h1>
          <p>
            Calculate your daily calorie intake needs based on your body metrics, activity level, and fitness goals to achieve optimal health and weight management.
          </p>
        </div>

        <div className="calculator-card">
          <div className="calculator-grid">
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Your Information
              </h2>

              {/* Weight Input */}
              <div className="input-group">
                <label>Weight ({unitSystem === "metric" ? "kg" : "lbs"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">⚖️</span>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="calc-input"
                    placeholder="70"
                  />
                </div>
                <div className="quick-select-group" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                  {[50, 60, 70, 80, 90].map((val) => (
                    <button
                      key={val}
                      onClick={() => setWeight(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Height Input */}
              <div className="input-group">
                <label>Height ({unitSystem === "metric" ? "cm" : "in"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📏</span>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="calc-input"
                    placeholder="170"
                  />
                </div>
                <div className="quick-select-group" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                  {[150, 160, 170, 180, 190].map((h) => (
                    <button
                      key={h}
                      onClick={() => setHeight(h)}
                      className="quick-select-btn success"
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age */}
              <div className="input-group">
                <label>Age</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎂</span>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    min="1"
                  />
                </div>
              </div>

              {/* Gender */}
              <div className="input-group">
                <label>Gender</label>
                <div className="quick-select-group">
                  {["male", "female"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      className={`quick-select-btn ${gender === g ? "primary" : "secondary"}`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Activity Level */}
              <div className="input-group">
                <label>Activity Level</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                  {[
                    { value: "sedentary", label: "Sedentary (little/no exercise)" },
                    { value: "light", label: "Light (exercise 1-3 days/week)" },
                    { value: "moderate", label: "Moderate (exercise 3-5 days/week)" },
                    { value: "active", label: "Active (exercise 6-7 days/week)" },
                    { value: "veryActive", label: "Very Active (intense daily)" },
                  ].map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setActivityLevel(level.value)}
                      className={`quick-select-btn ${activityLevel === level.value ? "primary" : "secondary"}`}
                      style={{ width: "100%", textAlign: "left", padding: '0.875rem 1rem' }}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Goal */}
              <div className="input-group">
                <label>Your Goal</label>
                <div className="quick-select-group" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  {[
                    { value: "lose", label: "Lose Weight" },
                    { value: "maintain", label: "Maintain" },
                    { value: "gain", label: "Gain Weight" },
                  ].map((g) => (
                    <button
                      key={g.value}
                      onClick={() => setGoal(g.value)}
                      className={`quick-select-btn ${goal === g.value ? "primary" : "secondary"}`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Unit System */}
              <div className="input-group">
                <label>Measurement System</label>
                <div className="quick-select-group">
                  {[
                    { value: "metric", label: "Metric (kg/cm)" },
                    { value: "imperial", label: "Imperial (lbs/in)" },
                  ].map((unit) => (
                    <button
                      key={unit.value}
                      onClick={() => setUnitSystem(unit.value)}
                      className={`quick-select-btn ${unitSystem === unit.value ? "primary" : "secondary"}`}
                    >
                      {unit.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateCalorieIntake} className="calculate-btn">
                Calculate Calorie Intake
              </button>
            </div>

            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Your Calorie Plan
              </h2>

              <div className="result-card">
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Maintenance Calories</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M16 8v5l3 3"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.maintenanceCalories}</div>
                  <div className="result-subtext">calories/day to maintain weight</div>
                </div>

                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Target Daily Intake</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.targetCalories}</div>
                  <div className="result-subtext">calories/day for your goal</div>
                </div>

                <div className="breakdown-section">
                  <h3 className="breakdown-title">Macronutrient Breakdown</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: "500" }}>
                    {result.macroBreakdown}
                  </p>
                </div>

                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Recommendation</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.interpretation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Calorie Intake</h3>
              <p>
                Calorie intake refers to the amount of energy you consume through food and beverages. Your body needs a specific number of calories to maintain, lose, or gain weight. 
                This calculator uses your Basal Metabolic Rate (BMR) and activity level to determine your Total Daily Energy Expenditure (TDEE), then adjusts based on your goals. 
                A balanced approach with proper macronutrient distribution ensures you meet your goals while maintaining optimal health.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>How Calorie Intake is Calculated:</h4>
            <div className="formula-display">
              Step 1: Calculate BMR using Mifflin-St Jeor Equation
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Step 2: Multiply BMR by Activity Factor = Maintenance Calories
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Step 3: Adjust for Goal (±500 calories for weight change)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Weight Loss</div>
                <div className="formula-param-value">TDEE - 500 cal</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Maintenance</div>
                <div className="formula-param-value">TDEE</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Weight Gain</div>
                <div className="formula-param-value">TDEE + 500 cal</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Safe Rate</div>
                <div className="formula-param-value">0.5-1 kg/week</div>
              </div>
            </div>
          </div>

          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How accurate is this calorie calculator?</h5>
              <p>
                This calculator provides a scientifically-backed estimate using the Mifflin-St Jeor equation. Individual results may vary based on metabolism, genetics, and other factors.
              </p>
            </div>

            <div className="faq-item">
              <h5>How should I track my calorie intake?</h5>
              <p>
                Use a food diary or mobile app to log meals. Weigh portions for accuracy, read nutrition labels, and track consistently for best results.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I eat fewer calories to lose weight faster?</h5>
              <p>
                Eating significantly below your BMR can slow metabolism and cause muscle loss. A 500-calorie deficit is safe and sustainable for most people.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are macronutrients?</h5>
              <p>
                Macronutrients are proteins (4 cal/g), carbohydrates (4 cal/g), and fats (9 cal/g). A balanced ratio ensures energy, muscle maintenance, and overall health.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I adjust my intake as I lose/gain weight?</h5>
              <p>
                Yes, recalculate your calorie needs every 5-10kg of weight change, as your BMR and TDEE will change with your body composition.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalorieIntakeCalculator;